import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { ArrowLeft, RefreshCw, Loader2, Check, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_DAYS = [0, 1, 6]; // Sunday=0, Monday=1, Saturday=6

export default function Reschedule() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    }
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      
      if (response.data.status !== 'confirmed') {
        setError('Only confirmed bookings can be rescheduled');
      } else if (response.data.reschedule_count >= 1) {
        setError('This booking has already been rescheduled once');
      } else if (response.data.reschedule_request) {
        setError('A reschedule request is already pending');
      }
    } catch (err) {
      setError('Booking not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedDate || !booking) return;

    const fetchAvailability = async () => {
      setLoadingSlots(true);
      setSelectedTime('');
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const addonStr = booking.addon_ids?.join(',') || '';
        const response = await axios.get(
          `${API}/availability/${dateStr}?service_id=${booking.service_id}&addon_ids=${addonStr}&exclude_booking_id=${bookingId}`
        );
        setTimeSlots(response.data.time_slots || []);
      } catch (error) {
        setTimeSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailability();
  }, [selectedDate, booking, bookingId]);

  const isDateDisabled = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable past dates
    if (date < today) return true;
    
    // Must be at least 24 hours in advance
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date < tomorrow) return true;
    
    // Only allow Monday (1), Saturday (6), Sunday (0)
    return ![0, 1, 6].includes(day);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/reschedule`, {
        booking_id: bookingId,
        new_date: selectedDate.toISOString().split('T')[0],
        new_time_slot: selectedTime
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit reschedule request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="noise-overlay" />
        <div className="max-w-md w-full text-center">
          <Link to="/" className="font-heading text-2xl font-bold text-[#C9A84C] inline-block mb-8">SAC CITY SHINE</Link>
          <div className="bg-surface border border-[#262626] p-8">
            <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-4">Reschedule Request Submitted</h1>
            <p className="text-gray-400 mb-6">
              Your request to reschedule to {selectedDate?.toLocaleDateString()} at {selectedTime} has been submitted. 
              We'll review it and notify you by email and SMS.
            </p>
            <Link to="/"><Button className="bg-white text-black hover:bg-gray-200 rounded-sm">Return to Home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="noise-overlay" />
        <div className="max-w-md w-full text-center">
          <Link to="/" className="font-heading text-2xl font-bold text-[#C9A84C] inline-block mb-8">SAC CITY SHINE</Link>
          <div className="bg-surface border border-[#262626] p-8">
            <h1 className="font-heading text-2xl font-bold text-white mb-4">Error</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <Link to="/"><Button className="bg-white text-black hover:bg-gray-200 rounded-sm">Return to Home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />
      
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight text-[#C9A84C]">SAC CITY SHINE</Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />Back to Home
          </Link>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <RefreshCw className="h-8 w-8" /> Reschedule Booking
            </h1>
            <p className="text-gray-400">Select a new date and time for your appointment</p>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-600/50 p-4 mb-8 text-center">
              <p className="text-red-400">{error}</p>
              <Link to="/"><Button variant="outline" className="mt-4 border-[#262626] text-white hover:bg-surface-highlight rounded-sm">Return to Home</Button></Link>
            </div>
          )}

          {!error && booking && (
            <div className="bg-surface border border-[#262626] p-8">
              {/* Current Booking Info */}
              <div className="mb-8 pb-8 border-b border-[#262626]">
                <h2 className="font-heading text-lg font-bold text-white mb-4">Current Appointment</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-400">Service:</span> <span className="text-white">{booking.service_name}</span></div>
                  <div><span className="text-gray-400">Date:</span> <span className="text-white">{booking.date}</span></div>
                  <div><span className="text-gray-400">Time:</span> <span className="text-white">{booking.time_slot} - {booking.end_time}</span></div>
                  <div><span className="text-gray-400">Duration:</span> <span className="text-white">{Math.floor(booking.total_duration_minutes / 60)}h {booking.total_duration_minutes % 60}m</span></div>
                </div>
              </div>

              {/* Date Selection */}
              <div className="mb-8">
                <h2 className="font-heading text-lg font-bold text-white mb-4">Select New Date</h2>
                <p className="text-gray-400 text-sm mb-4">Available: Monday, Saturday & Sunday (24+ hours in advance)</p>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={isDateDisabled}
                    className="bg-surface-highlight border border-[#262626] rounded-sm p-4"
                  />
                </div>
              </div>

              {/* Time Selection */}
              {selectedDate && (
                <div className="mb-8">
                  <h2 className="font-heading text-lg font-bold text-white mb-4">
                    Available Times for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </h2>
                  
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-white" />
                    </div>
                  ) : timeSlots.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => slot.available && setSelectedTime(slot.time)}
                          disabled={!slot.available}
                          className={`p-3 text-sm font-medium transition-all ${
                            selectedTime === slot.time
                              ? 'bg-white text-black'
                              : slot.available
                                ? 'bg-surface-highlight border border-[#262626] text-white hover:border-white'
                                : 'bg-[#1a1a1a] text-gray-600 cursor-not-allowed line-through'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center">No available times for this date</p>
                  )}
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-between items-center pt-8 border-t border-[#262626]">
                <Link to="/"><Button variant="outline" className="border-[#262626] text-white hover:bg-surface-highlight rounded-sm"><ArrowLeft className="mr-2 h-4 w-4" />Cancel</Button></Link>
                <Button 
                  onClick={handleSubmit}
                  disabled={!selectedDate || !selectedTime || submitting}
                  className="bg-white text-black hover:bg-gray-200 rounded-sm disabled:opacity-50"
                >
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : <>Request Reschedule<RefreshCw className="ml-2 h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
